<template>
    <div class="relative" ref="triggerElement" :class="[{'opacity-50 cursor-not-allowed': props.disabled}, props.theme]">
        <div class="flex items-center bg-white border border-gray-300 rounded-md hover:bg-gray-50 py-0.5 md:py-1 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600"
        :class="{'opacity-50 cursor-not-allowed': props.disabled}"
        >
            <div class="flex items-center ml-2 p-0.5 md:p-1 pr-1.5 md:pr-3 cursor-pointer border-r border-gray-300"
             @click="toggleScrolling"
             :class="{'!cursor-not-allowed': props.disabled}"
             >
                <svg class="w-4 h-4" :class="{ 'text-green-500 animate-pulse transition-all duration-300': isScrolling }" 
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
                </svg>
            </div>
            <button 
                @click="handleToggleDropdown" 
                data-scroll-button
                class="flex items-center justify-start md:justify-center px-1.5 py-0 md:px-3 md:py-1 text-sm font-medium text-gray-700 bg-white border-none bg-none hover:bg-gray-50 py-1 focus:outline-none dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600"
                :class="{ 'cursor-not-allowed': props.disabled }"
            >
                <div class="flex items-center ">
                    <div class="flex items-center hidden lg:flex">
                        Auto Scroll
                    </div>
                    <svg class="w-4 h-4 ml-1 lg:ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                </div>
            </button>
        </div>
        <Teleport to="body">
            <Transition>
                <div 
                    v-if="showScrollDropdown" 
                    ref="dropdownElement"
                    data-scroll-dropdown
                    class="fixed w-64 bg-white border border-gray-200 rounded-md shadow-lg dark:bg-gray-700 dark:border-gray-600 transition-all duration-300"
                    :style="dropdownStyle"
                >
                <div class="p-4">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-sm font-medium text-gray-900 dark:text-white">Auto Scroll Control</h3>
                        <button 
                            @click="toggleScrolling"
                            class="flex items-center px-3 py-1 text-xs font-medium rounded-md transition-colors"
                            :class="isScrolling 
                                ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900 dark:text-red-300 dark:hover:bg-red-800' 
                                : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900 dark:text-green-300 dark:hover:bg-green-800'"
                        >
                            <svg v-if="isScrolling" class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                            <svg v-else class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                            </svg>
                            {{ isScrolling ? 'Stop' : 'Start' }}
                        </button>
                    </div>
                    
                    <div class="space-y-4">
                        <div>
                            <!-- Volume Bar Visualization -->
                            <div class="flex items-end space-x-1 h-8 mb-2">
                                <div 
                                    v-for="(bar, index) in 5" 
                                    :key="index"
                                    @click="selectSpeedByBar(index)"
                                    class="flex-1 rounded-sm transition-all duration-200 cursor-pointer hover:opacity-80"
                                    :class="getBarClass(index)"
                                    :style="{ height: `${(index + 1) * 4}px` }"
                                ></div>
                            </div>
                            <div class="flex items-center space-x-2 w-full justify-between hidden md:flex">
                                <div @click="decreaseSpeed"
                                     :disabled="scrollSpeed <= 1" 
                                    class="cursor-pointer flex justify-center items-center rounded-full bg-white dark:bg-gray-500 p-2 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 transition-all hover:bg-grad  disabled:opacity-50 disabled:cursor-not-allowed">
                                    <svg class="w-3 h-3 text-indigo-700 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 2">
                                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 1h16"/>
                                    </svg>
                                </div>
                                <input 
                                    v-model.number="scrollSpeed"
                                    type="range"
                                    min="1"
                                    max="10"
                                    step="0.5"
                                    class="h-2 max-w-[150px] bg-gray-200 rounded-lg appearance-none cursor-pointer slider dark:bg-gray-600"
                                />
                                <div  @click="increaseSpeed"
                                      :disabled="scrollSpeed >= 10" 
                                class="cursor-pointer flex justify-center items-center rounded-full bg-white dark:bg-gray-500 p-2 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 transition-all hover:bg-grad  ">
                                    <svg class="w-3 h-3 text-indigo-700 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 18">
                                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 1v16M1 9h16"/>
                                    </svg>
                                </div>
                            </div>
                        </div>
                        
                                            <div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>Slowest</span>
                        <span>Fastest</span>
                    </div>
                    
                    <div class="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 hidden md:block">
                        <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">Keyboard shortcuts:</p>
                        <div class="grid grid-cols-1 gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                            <div><kbd class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-600 rounded font-mono text-[11px]">Page Down</kbd> Start / Speed up</div>
                            <div><kbd class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-600 rounded font-mono text-[11px]">Page Up</kbd> Slow down / Stop</div>
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </Transition>
        </Teleport>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, defineExpose, defineProps } from 'vue'
import { smoothScrollTo } from '@/service/smoothScroll'
import { useTeleportDropdown } from '@/composables/useTeleportDropdown.js'
import { appEvents } from '@/store/app.events';
import autoscrollStore from '@/store/autoscroll.store';

const isScrolling = ref(false)
const scrollSpeed = ref(1)
const scrollInterval = ref(null)
const scrollContainer = ref(null)
const props = defineProps({
    scrollElement: String,
    disabled: Boolean,
    theme: String,
    zIndex: {
        type: [String, Number],
        default: '200'
    }
})
const mapKeyPressed = ref({})

// Custom positioning function for responsive centering
const customPosition = (triggerRect, dropdownRect, gap) => {
    // Position below the trigger
    let top = triggerRect.bottom + gap
    let left = triggerRect.left
    
    // On medium screens and up, center it
    if (window.innerWidth >= 768) {
        left = triggerRect.left + (triggerRect.width / 2) - (dropdownRect.width / 2)
    } else {
        // On mobile, align to right edge of trigger
        left = triggerRect.right - dropdownRect.width
    }
    
    return { top, left }
}

const {
    isOpen: showScrollDropdown,
    triggerElement,
    dropdownElement,
    dropdownStyle,
    toggle: toggleScrollDropdown
} = useTeleportDropdown({
    position: 'bottom',
    zIndex: String(props.zIndex || '200'),
    customPosition
})

const toggleScrolling = () => {
    findScrollContainer();
    if (isScrolling.value) {
        stopScrolling()
        autoscrollStore.state.isScrolling = false;
    } else {
        // Clear any existing interval to ensure fresh start
        if (scrollInterval.value) {
            clearInterval(scrollInterval.value)
            scrollInterval.value = null;
            autoscrollStore.state.isScrolling = false;
        }
        startScrolling()
        autoscrollStore.state.isScrolling = true;
    }
    
    autoscrollStore.state.scrollSpeed = scrollSpeed.value;
    appEvents.emit('currentAutoScroll', {isScrolling: isScrolling.value, speed: scrollSpeed.value})
}

let scrollAccumulator = 0

const startScrolling = () => {
    findScrollContainer();
    if (scrollContainer.value) {
        isScrolling.value = true
        // Only reset to top if we're already at the bottom when starting fresh
        if (!scrollInterval.value) {
            const maxScroll = scrollContainer.value.scrollHeight - scrollContainer.value.clientHeight
            if (maxScroll > 0 && scrollContainer.value.scrollTop >= maxScroll - 2) {
                scrollContainer.value.scrollTop = 0
            }
        }
        scrollAccumulator = 0
        scrollContainer.value.style.scrollBehavior = 'auto'
        scrollInterval.value = setInterval(() => {
            if (scrollContainer.value) {
                const maxScroll = scrollContainer.value.scrollHeight - scrollContainer.value.clientHeight
                if (maxScroll <= 0) {
                    return
                }
                // Smooth scrolling step: scrollSpeed is 1 to 10
                scrollAccumulator += Math.max(0.75, scrollSpeed.value * 0.75)
                const step = Math.trunc(scrollAccumulator)
                if (step >= 1) {
                    scrollAccumulator -= step
                    scrollContainer.value.scrollTop += step
                }
                
                // Stop if we've reached the bottom
                if (scrollContainer.value.scrollTop >= maxScroll - 1) {
                    stopScrolling()
                }
            }
        }, 50) // Smooth interval: 20 updates per second
    }
}

const stopScrolling = () => {
    isScrolling.value = false
    autoscrollStore.state.isScrolling = false;
    scrollAccumulator = 0
    if (scrollContainer.value) {
        scrollContainer.value.style.scrollBehavior = ''
    }
    if (scrollInterval.value) {
        clearInterval(scrollInterval.value)
        scrollInterval.value = null
    }
}

const increaseSpeed = () => {
    if (scrollSpeed.value < 10) {
        scrollSpeed.value = Math.min(10, scrollSpeed.value + 0.5)
    }
    autoscrollStore.state.scrollSpeed = scrollSpeed.value;
}

const decreaseSpeed = () => {
    if (scrollSpeed.value > 1) {
        scrollSpeed.value = Math.max(1, scrollSpeed.value - 0.5)
    }
    autoscrollStore.state.scrollSpeed = scrollSpeed.value;
}

const selectSpeedByBar = (index) => {
    // Map bar index to speed: 0=1, 1=2, 2=3, 3=4, 4=5
    const speed = index + 1
    scrollSpeed.value = speed * 2
    autoscrollStore.state.scrollSpeed = scrollSpeed.value;
}

const getBarClass = (index) => {
    if (!isScrolling.value) {
        // Gray bars when stopped
        return 'bg-gray-300 dark:bg-gray-600'
    }
    
    // Calculate which bars should be colored based on speed
    const speedLevel = Math.ceil(scrollSpeed.value / 2) // 1-2, 3-4, 5-6, 7-8, 9-10
    const shouldColor = index < speedLevel
    
    if (!shouldColor) {
        return 'bg-gray-300 dark:bg-gray-600'
    }
    
    // Color bars based on speed level
    const colors = [
        'bg-green-400 dark:bg-green-500', // Slow (1-2)
        'bg-yellow-400 dark:bg-yellow-500', // Medium (3-4)
        'bg-orange-400 dark:bg-orange-500', // Fast (5-6)
        'bg-red-400 dark:bg-red-500', // Very Fast (7-8)
        'bg-purple-400 dark:bg-purple-500' // Maximum (9-10)
    ]
    
    return colors[index] || 'bg-gray-300 dark:bg-gray-600'
}

// Click outside is handled by useTeleportDropdown composable

const isControlActive = () => {
    const parentModal = triggerElement.value?.closest('[data-modal-container], [id^="modal-"]')
    if (parentModal) {
        return !parentModal.classList.contains('hidden') && parentModal.clientHeight > 0
    }
    const openModalWithScroll = Array.from(document.querySelectorAll('[data-modal-container], [id^="modal-"]'))
        .some(el => !el.classList.contains('hidden') && el.clientHeight > 0 && el.querySelector('[data-modal-body]'))
    return !openModalWithScroll
}

const handleKeydown = (event) => {
    if (!isControlActive()) return
    // Track pressed keys
    mapKeyPressed.value[event.key] = true
    // Only handle keyboard shortcuts when not in an input field
    if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA' 
        || event.target.getAttribute('contenteditable') === 'true') {
        return
    }
    
    if (event.key === 'PageDown') {
        event.preventDefault()
        if (!isScrolling.value) {
            toggleScrolling()
        } else {
            increaseSpeed()
        }
    } else if (event.key === 'PageUp') {
        event.preventDefault()
        if (isScrolling.value) {
            if (scrollSpeed.value <= 1) {
                toggleScrolling()
            } else {
                decreaseSpeed()
            }
        }
    }
}

const handleKeyUp = (event) => {
    // Remove key from map when released
    delete mapKeyPressed.value[event.key]
}  

// Watch for scroll speed changes - no need to restart scrolling, just let the interval continue with new speed
watch(scrollSpeed, () => {
    // Speed changes are handled automatically by the interval using the reactive scrollSpeed.value
    // No need to restart scrolling as it would reset the position
})

watch(() =>props.scrollElement, () => {
    findScrollContainer()
})

// Position calculation and resize handling are handled by useTeleportDropdown composable

// Find the scrollable container
const findScrollContainer = () => {
    // 1. If this ScrollControl is inside a modal, scope the lookup to that modal first
    const parentModal = triggerElement.value?.closest('[data-modal-container], [id^="modal-"]')
    if (parentModal) {
        const modalTarget = (props.scrollElement ? parentModal.querySelector(props.scrollElement) : null)
            || parentModal.querySelector('[data-modal-body]')
        if (modalTarget) {
            if (modalTarget.scrollHeight <= modalTarget.clientHeight) {
                const splitWrapper = modalTarget.querySelector('.song-viewer-wrapper')
                if (splitWrapper && splitWrapper.scrollHeight > splitWrapper.clientHeight) {
                    scrollContainer.value = splitWrapper
                    return
                }
            }
            scrollContainer.value = modalTarget
            return
        }
    }

    // 2. If an explicit selector was provided, prefer a visible/scrollable match
    if (props.scrollElement) {
        const candidates = Array.from(document.querySelectorAll(props.scrollElement))
        const explicitTarget = candidates.find(el => el.clientHeight > 0 && el.scrollHeight > el.clientHeight)
            || candidates.find(el => el.clientHeight > 0)
            || candidates[0]
        if (explicitTarget) {
            scrollContainer.value = explicitTarget
            return
        }
    }
    
    // 3. If a visible modal with data-modal-body is open, target that
    const modalBodies = Array.from(document.querySelectorAll('[data-modal-body]'))
    const visibleModalBody = modalBodies.find(el => el.clientHeight > 0 && el.scrollHeight > el.clientHeight)
        || modalBodies.find(el => el.clientHeight > 0)
    if (visibleModalBody) {
        scrollContainer.value = visibleModalBody
        return
    }

    // 4. Look for the main content area that contains the song
    const mainContainer = document.querySelector('.main') || document.querySelector('.card')
    if (mainContainer && mainContainer.scrollHeight > mainContainer.clientHeight) {
        scrollContainer.value = mainContainer
    } else {
        // 5. Fallback to the document element / window
        scrollContainer.value = document.scrollingElement || document.documentElement
    }
}

const handleToggleDropdown = () => {
    if (props.disabled) return
    toggleScrollDropdown()
}

const handleToggleAutoScroll = () => {
    if (!isControlActive()) return
    toggleScrolling();
}
onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
    document.addEventListener('keyup', handleKeyUp)
    // Find scroll container after a short delay to ensure DOM is ready
    setTimeout(() => {
        findScrollContainer()
        // Reset scroll position when component mounts
        if (scrollContainer.value) {
            smoothScrollTo(scrollContainer.value, {
                top: 0,
                left: 0,
                behavior: 'smooth'
            })
        }
    }, 100)
    appEvents.on('toggleAutoScroll', handleToggleAutoScroll);
})

const unwatchIsScrolling = autoscrollStore.watch((state) => state.isScrolling, (newVal) => {
    if (!newVal) {
        stopScrolling();
    } else if (scrollInterval.value) {
        isScrolling.value = true;
    }
})
const unwatchScrollSpeed = autoscrollStore.watch((state) => state.scrollSpeed, (newVal) => {
    scrollSpeed.value = newVal
})

onUnmounted(() => {
    appEvents.off('toggleAutoScroll', handleToggleAutoScroll);
    document.removeEventListener('keydown', handleKeydown)
    document.removeEventListener('keyup', handleKeyUp)
    if (typeof unwatchIsScrolling === 'function') unwatchIsScrolling()
    if (typeof unwatchScrollSpeed === 'function') unwatchScrollSpeed()
    stopScrolling()
})

// Expose methods for external control
defineExpose({ 
    startScrolling, 
    stopScrolling, 
    isScrolling: () => isScrolling.value,
    setSpeed: (speed) => { scrollSpeed.value = Math.max(1, Math.min(10, speed)) },
    resetScroll: () => {
        if (scrollContainer.value) {
            smoothScrollTo(scrollContainer.value, {
                top: 0,
                left: 0,
                behavior: 'smooth'
            })
        }
    },
    smoothScrollTo: (top, left = 0) => {
        if (scrollContainer.value) {
            smoothScrollTo(scrollContainer.value, {
                top,
                left,
                behavior: 'smooth'
            })
        }
    },
    smoothScrollToBottom: () => {
        if (scrollContainer.value) {
            smoothScrollTo(scrollContainer.value, {
                top: scrollContainer.value.scrollHeight,
                left: 0,
                behavior: 'smooth'
            })
        }
    },
    findScrollContainer
})
</script>

<style scoped>
.slider {
    -webkit-appearance: none;
    appearance: none;
}

.slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #6366f1;
    cursor: pointer;
}

.slider::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #6366f1;
    cursor: pointer;
    border: none;
}

.slider::-webkit-slider-track {
    background: #e5e7eb;
    border-radius: 8px;
    height: 8px;
}

.slider::-moz-range-track {
    background: #e5e7eb;
    border-radius: 8px;
    height: 8px;
}

.dark .slider::-webkit-slider-track {
    background: #4b5563;
}

.dark .slider::-moz-range-track {
    background: #4b5563;
}
</style> 